import lodashIsNumber from 'lodash-es/isNumber';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import { AssetDoors, AssetType, AuctionState, ComputedState, DirectSaleComputedState, DirectSaleState, type SaleAssetBusEntity } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, type PlatformSaleItemEntity, type PlatformSellingMethod } from '~/types/sale.type';

export type UseSdpSchemaOrgReturnType = void;

// schema.org counts door openings, not leaves: "1+2+1" is 3 doors
const DOORS_TO_COUNT_MAP: Record<AssetDoors, number> = {
    [AssetDoors.Single]: 1,
    [AssetDoors.Double]: 1,
    [AssetDoors.SingleSingle]: 2,
    [AssetDoors.SingleDouble]: 2,
    [AssetDoors.DoubleSingle]: 2,
    [AssetDoors.DoubleDouble]: 2,
    [AssetDoors.SingleSingleSingle]: 3,
    [AssetDoors.SingleDoubleSingle]: 3,
    [AssetDoors.SingleDoubleDouble]: 3,
    [AssetDoors.DoubleSingleSingle]: 3,
    [AssetDoors.DoubleDoubleSingle]: 3,
    [AssetDoors.DoubleDoubleDouble]: 3,
    [AssetDoors.DoubleDoubleDoubleDouble]: 4,
};

const SCHEMA_ORG_MAX_IMAGES = 6;
const SCHEMA_ORG_IMAGE_WIDTH = 1280; // The imageCdn provider maps this width to the `_lg.webp` variant

// The states the sitemap publishes (`getSitemapAuctionsListing` / `getSitemapDirectSalesListing`)
const PUBLIC_AUCTION_STATES: AuctionState[] = [AuctionState.Pending, AuctionState.Running, AuctionState.Expired];
const PUBLIC_DIRECT_SALE_STATES: DirectSaleState[] = [DirectSaleState.Open, DirectSaleState.Closed];

type SchemaOrgAvailability = 'InStock' | 'OutOfStock' | 'PreOrder' | 'SoldOut';

export const useSdpSchemaOrg = (sale: Ref<PlatformSaleItemEntity>): UseSdpSchemaOrgReturnType => {
    // COMPOSABLES
    const { tt, formatUnits } = useTypedI18n();
    const image = useImage();
    const localePath = useLocalePath();
    const { frontendBaseUrl } = useRuntimeConfig().public;
    const { formatSlpRoute } = useSlpRouteUtils();
    const { isBundleMono, isBundleMulti } = useSaleType(() => sale.value.type);
    const { title: saleTitle } = useSaleTitle(() => sale.value);

    // DATA
    const mainAsset = computed<SaleAssetBusEntity>(() => sale.value.mainAsset);
    const assetType = computed<AssetType>(() => mainAsset.value.assetType || AssetType.Bus);
    const sellingMethod = computed<PlatformSellingMethod>(() => sale.value.sellingMethod);
    const registrationYear = computed<number | undefined>(() =>
        mainAsset.value.firstRegistration ? new Date(mainAsset.value.firstRegistration).getUTCFullYear() : undefined
    );

    const detailUrl = computed<string>(() => {
        const name = isAuction(sale.value) ? ROUTE_NAMES.AUCTION_DETAIL : ROUTE_NAMES.DIRECT_SALE_DETAIL;

        return `${frontendBaseUrl}${localePath({ name, params: { slug: sale.value.slug } })}`;
    });

    const images = computed<string[]>(() =>
        [...new Set([...mainAsset.value.images.cover, ...mainAsset.value.images.base])]
            .slice(0, SCHEMA_ORG_MAX_IMAGES)
            .map(mediaKey => image(mediaKey, { width: SCHEMA_ORG_IMAGE_WIDTH }))
    );

    const productName = computed<string>(() => {
        const vehicle = isBundleMono.value
            ? saleTitle.value // Already prefixed with the asset count, e.g. "N. 3 Mercedes-Benz Vario"
            : `${tt(`asset.type.${assetType.value}`, 1)} ${mainAsset.value.brand.brandValue} ${mainAsset.value.model.modelValue}`;

        return [vehicle, registrationYear.value?.toString(), formatUnits.km(mainAsset.value.km)].filter(Boolean).join(' ');
    });

    // Mirrors SdpSalePanelStatusSection: never advertise a price the page itself does not show.
    const price = computed<number | undefined>(() => {
        if (isAuction(sale.value)) {
            return sale.value.clearingPrice ?? sale.value.currentPrice ?? undefined;
        }

        if (sale.value.computedState === DirectSaleComputedState.NoActiveOrder && sale.value.buyNowPrice) {
            return sale.value.buyNowPrice;
        }

        if (!sale.value.price) {
            return undefined;
        }

        return sale.value.clearingPrice ?? sale.value.price;
    });

    // `SoldOut` is reserved for an actual sale: anything else no longer purchasable reports `OutOfStock`
    const availability = computed<SchemaOrgAvailability>(() => {
        if (isAuction(sale.value)) {
            if (sale.value.computedState === ComputedState.Sold || sale.value.computedState === ComputedState.PendingPayment) return 'SoldOut';
            if (sale.value.state === AuctionState.Running) return 'InStock';
            if (sale.value.state === AuctionState.Pending) return 'PreOrder';

            return 'OutOfStock';
        }

        if (sale.value.computedState === DirectSaleComputedState.Sold || sale.value.computedState === DirectSaleComputedState.PendingPayment) {
            return 'SoldOut';
        }

        if (sale.value.state === DirectSaleState.Open && sale.value.computedState === DirectSaleComputedState.NoActiveOrder) return 'InStock';

        return 'OutOfStock';
    });

    const priceValidUntil = computed<string | undefined>(() => sale.value.expiresAt ?? undefined);

    const offer = computed(() => ({
        '@type': 'Offer',
        // `shouldHidePrice` strips every price for anonymous visitors, so to a crawler this Offer has none: accepted, at the cost of Google's "Missing field price"
        ...setIf(lodashIsNumber(price.value), () => ({ price: price.value!, priceCurrency: sale.value.currency })),
        availability: availability.value, // Bare token: the offer resolver prefixes it with `https://schema.org/`
        itemCondition: 'UsedCondition',
        url: detailUrl.value,
        ...setIf(!!priceValidUntil.value, () => ({ priceValidUntil: priceValidUntil.value! })),
        // Inline Organization rather than an `@id` reference: `seller` is not run through a resolver
        seller: { '@type': 'Organization', name: 'Fleequid', url: frontendBaseUrl },
    }));

    const product = computed(() =>
        defineProduct({
            '@type': ['Product', 'BusOrCoach'],
            // `description` is intentionally absent: it is inherited from the `<meta name="description">` set by `useSdpSeoMeta`
            name: productName.value,
            image: images.value,
            url: detailUrl.value,
            // Without an explicit brand the resolver would fall back to the Fleequid organization
            brand: { '@type': 'Brand', name: mainAsset.value.brand.brandValue },
            model: mainAsset.value.model.modelValue,
            vehicleIdentificationNumber: mainAsset.value.vin,
            itemCondition: 'https://schema.org/UsedCondition', // Full url: the product resolver does not prefix it, unlike the offer one
            mileageFromOdometer: { '@type': 'QuantitativeValue', value: mainAsset.value.km, unitCode: 'KMT' },
            ...setIf(!!registrationYear.value, () => ({
                dateVehicleFirstRegistered: mainAsset.value.firstRegistration,
                vehicleModelDate: String(registrationYear.value),
            })),
            fuelType: tt(`asset.fuel.${mainAsset.value.fuel}`),
            numberOfDoors: DOORS_TO_COUNT_MAP[mainAsset.value.doors],
            vehicleSeatingCapacity: mainAsset.value.seats,
            ...setIf(!!mainAsset.value.euroNorm, () => ({ meetsEmissionStandard: tt(`asset.euroNorm.${mainAsset.value.euroNorm!}`) })),
            ...setIf(!!mainAsset.value.power, () => ({
                vehicleEngine: {
                    '@type': 'EngineSpecification',
                    enginePower: { '@type': 'QuantitativeValue', value: mainAsset.value.power, unitCode: 'KWT' },
                },
            })),
            offers: offer.value,
        })
    );

    // `query: {}` strips the default listing filters injected by `formatSlpRoute`: the breadcrumb must point at the plain listing url
    const absoluteSlpUrl = (brandUrlKey?: string): string =>
        `${frontendBaseUrl}${localePath({ ...formatSlpRoute({ sellingMethod: sellingMethod.value, assetType: assetType.value, brandUrlKey }), query: {} })}`;

    const breadcrumb = computed(() =>
        defineBreadcrumb({
            itemListElement: [
                { name: tt('homepage.link'), item: `${frontendBaseUrl}${localePath(STORYBLOK_ROUTES.HOMEPAGE)}` },
                {
                    name: isAuction(sale.value) ? tt('slp.navigation.auction') : tt('slp.navigation.directSale'),
                    item: absoluteSlpUrl(),
                },
                { name: mainAsset.value.brand.brandValue, item: absoluteSlpUrl(mainAsset.value.brand.urlKey) },
                { name: saleTitle.value }, // Last crumb: no link to the current page
            ],
        })
    );

    const isPubliclyListed = computed<boolean>(() =>
        isAuction(sale.value) ? PUBLIC_AUCTION_STATES.includes(sale.value.state) : PUBLIC_DIRECT_SALE_STATES.includes(sale.value.state)
    );

    // A multi version bundle describes the main asset only; `image` is required on Product and an empty array is not stripped
    const hasProduct = computed<boolean>(
        () => !isBundleMulti.value && !sale.value.hiddenFromListing && isPubliclyListed.value && !!images.value.length
    );

    // SEO
    useSchemaOrg([...insertIf(hasProduct.value, () => product.value), breadcrumb.value]);
};
